import React, { memo, SetStateAction, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  EmptyState,
  Box as FormRow,
  Icon,
  Input,
  RowsSkeletonLoader,
} from '@node-space/ui-components'
import { Lottie } from 'components/Animations/SimpleLottie'
import { ExternalLink } from 'components/ExternalLink/ExternalLink'
import NoSearchResults from 'components/NoSearchResults/NoSearchResults'
import { WALLETS_NUMBER_OF_ROWS } from 'constants/General'
import { useWalletControllerContext } from 'hooks/useWalletControllerContext'
import { WalletType } from 'types/types'
import { TWalletType, WalletTypes } from 'types/wallets'
import { WalletAction } from '../Modal/WalletModal'
import { WalletSheet } from '../Sheet/WalletSheet'
import WalletList from './WalletList'

interface WalletTableProps {
  testid: string
  wallets: WalletType[]
  walletType: TWalletType
  setWalletModalAction: React.Dispatch<SetStateAction<WalletAction>>
  searchQuery?: string
  setShowWalletModal?: React.Dispatch<SetStateAction<boolean>>
  clearFilters?: () => void
  onSearchTermChange?: React.Dispatch<SetStateAction<string>>
  isWalletBeingSearched?: boolean
  debouncedSearch?: string
}

const WalletTable = ({
  searchQuery,
  onSearchTermChange,
  wallets,
  walletType,
  setShowWalletModal,
  clearFilters,
  setWalletModalAction,
  isWalletBeingSearched,
  debouncedSearch,
}: WalletTableProps) => {
  const { t } = useTranslation()
  const [isSheetOpen, setIsSheetOpen] = useState(false)

  const { isLoading, isSuccess, hasErrors } = useWalletControllerContext()

  if (hasErrors) {
    return (
      <EmptyState
        testId="wallets-loading-error"
        LottiePlayer={<Lottie src="connection-error" />}
        title={t('requestErrors.pageErrorIntro')}
        description={
          <span>
            {`${t('requestErrors.comeBackLater')} `}
            <Button variant="ghost" noPadding onClick={() => window.location.reload()}>
              {t('requestErrors.refreshPage')}
            </Button>
          </span>
        }
      />
    )
  }

  return (
    <>
      <Box paddingB={24} flex>
        <FormRow width="full">
          <Input
            searchable
            testId="filter-search-field"
            placeholderText={t('walletsPage.searchPlaceholder')}
            onChange={event => onSearchTermChange?.(event.target.value)}
            value={searchQuery || ''}
            iconPosition="right"
            IconButton={
              searchQuery && (
                <Button
                  variant="ghost"
                  onClick={() => onSearchTermChange?.('')}
                  iconElement={<Icon name="X" />}
                />
              )
            }
            {...(!wallets?.length && !searchQuery?.length && { disabled: true })}
          />
        </FormRow>
      </Box>

      {!wallets?.length && isWalletBeingSearched && !isLoading && (
        <NoSearchResults clearSearchCriteria={clearFilters} />
      )}

      {isSuccess && wallets?.length === 0 && !isLoading && !debouncedSearch && (
        <EmptyState
          testId="no-wallets-content"
          LottiePlayer={<Lottie src="no-data" />}
          title={t('wallets.emptyStateForWalletsHeadline')}
          description={
            walletType === WalletTypes.FIAT ? (
              <span>
                {t('createFiatWalletDisclaimer')}
                <ExternalLink href="https://help.bvnk.com/hc/en-us/articles/13995679766930">
                  {` ${t('learnMore')}`}
                </ExternalLink>
              </span>
            ) : (
              t('wallets.emptyStateForWalletsDescription')
            )
          }
        />
      )}

      {isLoading && <RowsSkeletonLoader rows={WALLETS_NUMBER_OF_ROWS} />}

      {!isLoading && wallets?.length > 0 && (
        <WalletList
          setIsSheetOpen={setIsSheetOpen}
          wallets={wallets}
          walletType={walletType}
          setWalletModalAction={setWalletModalAction}
          setShowWalletModal={setShowWalletModal}
        />
      )}
      <WalletSheet isSheetOpen={isSheetOpen} />
    </>
  )
}

export default memo(WalletTable)
